import { useState, useRef, useCallback, useEffect } from 'react'
import { Mic, MicOff, Loader2 } from 'lucide-react'
import {
  isWebSpeechAvailable,
  isWhisperFallbackEnabled,
  createRecognitionSession,
  createAudioRecorder,
  transcribeWithWhisper,
  type TranscriptResult,
  type RecognitionStatus,
} from '../../lib/services/voiceTranscript'

interface VoiceRecorderProps {
  onTranscript: (result: TranscriptResult) => void
  /** Also capture raw audio blob for Whisper fallback / archival */
  captureAudio?: boolean
  onAudioBlob?: (blob: Blob) => void
}

export function VoiceRecorder({ onTranscript, captureAudio = false, onAudioBlob }: VoiceRecorderProps) {
  const [status, setStatus] = useState<RecognitionStatus>('idle')
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [capturing, setCapturing] = useState(false)

  const recognitionRef = useRef<ReturnType<typeof createRecognitionSession> | null>(null)
  const recorderRef = useRef<ReturnType<typeof createAudioRecorder> | null>(null)

  useEffect(() => () => {
    recognitionRef.current?.stop()
    recognitionRef.current = null
    const recorder = recorderRef.current
    if (recorder?.isRecording()) void recorder.stop().catch(() => {})
    recorderRef.current = null
  }, [])

  const handleStart = useCallback(async () => {
    setError(null)
    setInterim('')

    if (isWebSpeechAvailable()) {
      const session = createRecognitionSession({
        onInterim: setInterim,
        onFinal: (result) => {
          setInterim('')
          onTranscript(result)
        },
        onStatusChange: setStatus,
        onError: setError,
      })
      recognitionRef.current = session
      session.start()
    }

    if (captureAudio) {
      try {
        const recorder = createAudioRecorder()
        recorderRef.current = recorder
        await recorder.start()
        setCapturing(true)
      } catch {
        setError('Microphone access denied')
      }
    }
  }, [captureAudio, onTranscript])

  const handleStop = useCallback(async () => {
    setCapturing(false)
    recognitionRef.current?.stop()
    recognitionRef.current = null

    if (recorderRef.current?.isRecording()) {
      try {
        const blob = await recorderRef.current.stop()
        onAudioBlob?.(blob)

        // Whisper fallback only when explicitly enabled (#1804 — no edge fn yet)
        if (!isWebSpeechAvailable()) {
          if (!isWhisperFallbackEnabled()) {
            setError('Voice transcription unavailable in this browser (Whisper fallback not configured)')
          } else {
            setStatus('processing')
            try {
              const result = await transcribeWithWhisper(blob)
              onTranscript(result)
            } catch {
              setError('Transcription failed — audio saved for later')
            }
            setStatus('idle')
          }
        }
      } catch {
        // Recorder wasn't active
      }
      recorderRef.current = null
    }
  }, [onTranscript, onAudioBlob])

  const isActive = status === 'listening' || status === 'processing' || capturing

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={isActive ? handleStop : handleStart}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-[0.98] ${
            isActive
              ? 'bg-red-500 text-white shadow-md'
              : 'bg-[var(--brand-primary)] text-white shadow-sm hover:bg-[var(--brand-deep)]'
          }`}
        >
          {status === 'processing' ? (
            <Loader2 size={16} className="animate-spin" />
          ) : isActive ? (
            <MicOff size={16} />
          ) : (
            <Mic size={16} />
          )}
          {status === 'processing' ? 'Processing…' : isActive ? 'Stop' : 'Record'}
        </button>

        {isActive && (
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-semibold text-red-600">Listening</span>
          </span>
        )}
      </div>

      {interim && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 italic">
          {interim}
        </p>
      )}

      {error && (
        <p className="text-xs font-medium text-red-600">{error}</p>
      )}

      {!isWebSpeechAvailable() && status === 'idle' && (
        <p className="text-[10px] text-slate-400">
          {isWhisperFallbackEnabled()
            ? 'Live transcription unavailable — audio can use Whisper fallback when configured.'
            : 'Live transcription unavailable in this browser (Whisper fallback not configured).'}
        </p>
      )}
    </div>
  )
}
